import { z } from 'zod';

/**
 * Contact Form Validation Schema
 *
 * Validates all 11 form fields with appropriate rules:
 * - Type checking (string, number, enum)
 * - Length limits (min/max)
 * - Format validation (regex for name, email, phone)
 * - Required vs optional fields
 */
export const contactFormSchema = z.object({
  // Required: Full Name
  name: z.string()
    .min(2, 'Name must be at least 2 characters')
    .max(100, 'Name must be less than 100 characters')
    .regex(/^[a-zA-ZÀ-ÿ\s'-]+$/u, 'Name contains invalid characters'),

  // Required: Email
  email: z.string()
    .email('Invalid email address')
    .max(255, 'Email is too long')
    .toLowerCase(),

  // Optional: Phone
  phone: z.string()
    .max(20, 'Phone number is too long')
    .regex(/^[+\d\s()-]*$/, 'Phone contains invalid characters')
    .optional()
    .or(z.literal('')),

  // Optional: Country
  country: z.string()
    .max(100, 'Country name is too long')
    .optional()
    .or(z.literal('')),

  // Required: Experience Type (dropdown)
  experience: z.enum([
    'heli-skiing',
    'ski-trips',
    'randonnee',
    'fly-fishing',
    'lodge-stay',
    'multi-sport',
    'custom'
  ]),

  // Optional: Start Date
  startDate: z.string()
    .optional()
    .or(z.literal('')),

  // Optional: Duration (1-30 days)
  duration: z.union([
    z.coerce.number()
      .int('Duration must be a whole number')
      .min(1, 'Duration must be at least 1 day')
      .max(30, 'Duration cannot exceed 30 days'),
    z.literal('')
  ]).optional(),

  // Optional: Group Size (1-20 people)
  groupSize: z.union([
    z.coerce.number()
      .int('Group size must be a whole number')
      .min(1, 'Group size must be at least 1 person')
      .max(20, 'Group size cannot exceed 20 people'),
    z.literal('')
  ]).optional(),

  // Optional: Experience Level (dropdown)
  level: z.enum([
    'beginner',
    'intermediate',
    'advanced',
    'expert'
  ]).optional().or(z.literal('')),

  // Required: Message (10-5000 characters)
  message: z.string()
    .min(10, 'Message must be at least 10 characters')
    .max(5000, 'Message is too long (max 5000 characters)')
    .trim(),

  // Optional: Budget Range (dropdown)
  budget: z.enum([
    '5000-10000',
    '10000-20000',
    '20000-50000',
    '50000+'
  ]).optional().or(z.literal(''))
});

/**
 * TypeScript type derived from the Zod schema
 * Use this for type-safe form data handling
 */
export type ContactFormData = z.infer<typeof contactFormSchema>;
